import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Wallet, ArrowUpRight, ArrowDownRight, Plus, Gift,
  Copy, Share2, Clock, CheckCircle, XCircle, Upload,
  ArrowLeft, RefreshCw, QrCode, CreditCard
} from 'lucide-react';
import WalletRedemptionSection from './WalletRedemptionSection';
import { initiatePayUPayment } from '../lib/payu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientWalletPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState({ balance: 0, total_topups: 0, total_spent: 0 });
  const [transactions, setTransactions] = useState([]);
  const [topupRequests, setTopupRequests] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [qrConfig, setQrConfig] = useState(null);
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupUtr, setTopupUtr] = useState('');
  const [topupScreenshot, setTopupScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payuLoading, setPayuLoading] = useState(false);

  const handlePayUTopup = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setPayuLoading(true);
    try {
      await initiatePayUPayment({ token: getToken(), purpose: 'patient_wallet', amount: topupAmount });
    } catch (err) {
      toast.error(err.message || 'Failed to start PayU payment');
      setPayuLoading(false);
    }
  };

  const getToken = () => localStorage.getItem('patient_token');

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate('/patient/login');
      return;
    }

    try {
      // Fetch all data in parallel
      const [walletRes, txnRes, topupRes, refRes, refListRes, qrRes] = await Promise.all([
        fetch(`${API_URL}/api/patient-wallet/balance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-wallet/transactions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-wallet/topup/my-requests`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-referral/my-code`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-referral/my-referrals`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/wallet/qr-config`).catch(() => null)
      ]);

      // Process responses
      if (walletRes?.ok) setWallet(await walletRes.json());
      if (txnRes?.ok) { const d = await txnRes.json(); setTransactions(d.transactions || []); }
      if (topupRes?.ok) { const d = await topupRes.json(); setTopupRequests(d.requests || []); }
      if (refRes?.ok) { const d = await refRes.json(); setReferralCode(d.code); }
      if (refListRes?.ok) { const d = await refListRes.json(); setReferrals(d.referrals || []); }
      if (qrRes?.ok) { const d = await qrRes.json(); if (d.has_config) setQrConfig(d.config); }

    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load wallet data');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTopupSubmit = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', topupAmount);
      if (topupUtr) formData.append('utr_number', topupUtr);
      if (topupScreenshot) formData.append('screenshot', topupScreenshot);

      const res = await fetch(`${API_URL}/api/patient-wallet/topup/request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Top-up request submitted!');
        setShowTopupDialog(false);
        setTopupAmount('');
        setTopupUtr('');
        setTopupScreenshot(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setSubmitting(false);
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const shareReferralCode = async () => {
    const shareUrl = `${window.location.origin}/patient/login?ref=${referralCode}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: '1Clin Patient Portal',
          text: `Join 1Clin using my referral code: ${referralCode}`,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyReferralCode();
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/patient/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-bold text-lg">My Wallet</h1>
            <p className="text-sm text-muted-foreground">Manage your balance and transactions</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Balance Card */}
        <Card className="bg-gradient-to-r from-teal-600 to-teal-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Available Balance</p>
                <p className="text-4xl font-bold">₹{wallet.balance?.toFixed(2) || '0.00'}</p>
              </div>
              <Dialog open={showTopupDialog} onOpenChange={setShowTopupDialog}>
                <DialogTrigger asChild>
                  <Button variant="secondary" className="gap-2">
                    <Plus className="h-4 w-4" /> Add Money
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Money to Wallet</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {qrConfig && (
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-2">Scan QR to pay</p>
                        {qrConfig.qr_image_url ? (
                          <img src={qrConfig.qr_image_url} alt="UPI QR" className="w-48 h-48 mx-auto" />
                        ) : (
                          <div className="w-48 h-48 mx-auto bg-slate-200 rounded flex items-center justify-center">
                            <QrCode className="h-12 w-12 text-slate-400" />
                          </div>
                        )}
                        <p className="text-sm font-medium mt-2">{qrConfig.payee_name}</p>
                        <p className="text-xs text-muted-foreground">{qrConfig.upi_id}</p>
                      </div>
                    )}
                    
                    <div>
                      <label className="text-sm font-medium">Amount (₹)</label>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={topupAmount}
                        onChange={(e) => setTopupAmount(e.target.value)}
                      />
                    </div>

                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={handlePayUTopup}
                      disabled={payuLoading || !topupAmount}
                      data-testid="payu-instant-recharge-btn"
                    >
                      {payuLoading ? 'Redirecting...' : 'Pay Instantly via PayU'}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">OR submit manual UTR details below</p>
                    
                    <div>
                      <label className="text-sm font-medium">UTR/Reference Number (Optional)</label>
                      <Input
                        placeholder="12-digit UTR"
                        value={topupUtr}
                        onChange={(e) => setTopupUtr(e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Payment Screenshot (Optional)</label>
                      <div className="mt-1">
                        <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50">
                          <Upload className="h-5 w-5" />
                          <span>{topupScreenshot ? topupScreenshot.name : 'Upload Screenshot'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => setTopupScreenshot(e.target.files[0])}
                          />
                        </label>
                      </div>
                    </div>
                    
                    <Button
                      className="w-full"
                      onClick={handleTopupSubmit}
                      disabled={submitting || !topupAmount}
                    >
                      {submitting ? 'Submitting...' : 'Submit Top-up Request'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-sm opacity-80">Total Top-ups</p>
                <p className="text-xl font-semibold">₹{wallet.total_topups?.toFixed(0) || 0}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-sm opacity-80">Total Spent</p>
                <p className="text-xl font-semibold">₹{wallet.total_spent?.toFixed(0) || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="transactions">
          <TabsList className="w-full">
            <TabsTrigger value="transactions" className="flex-1">Transactions</TabsTrigger>
            <TabsTrigger value="topups" className="flex-1">Top-ups</TabsTrigger>
            <TabsTrigger value="referrals" className="flex-1">Referrals</TabsTrigger>
            <TabsTrigger value="redeem" className="flex-1">Redeem</TabsTrigger>
          </TabsList>

          <TabsContent value="transactions" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No transactions yet</p>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((txn) => (
                      <div key={txn.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${txn.amount > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                            {txn.amount > 0 ? (
                              <ArrowDownRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ArrowUpRight className="h-4 w-4 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{txn.description || txn.type}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(txn.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <p className={`font-semibold ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.amount > 0 ? '+' : ''}₹{Math.abs(txn.amount).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="topups" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top-up Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {topupRequests.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No top-up requests</p>
                ) : (
                  <div className="space-y-3">
                    {topupRequests.map((req) => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">₹{req.amount}</p>
                          <p className="text-xs text-muted-foreground">
                            {req.utr_number || 'No UTR'} • {new Date(req.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant={
                          req.status === 'approved' ? 'default' :
                          req.status === 'rejected' ? 'destructive' : 'outline'
                        }>
                          {req.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {req.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                          {req.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                          {req.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals" className="mt-4 space-y-4">
            {/* Referral Code Card */}
            <Card className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-80 flex items-center gap-2">
                      <Gift className="h-4 w-4" /> Your Referral Code
                    </p>
                    <p className="text-3xl font-bold tracking-wider mt-1">{referralCode || 'N/A'}</p>
                    <p className="text-sm opacity-80 mt-2">Share and earn rewards!</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="icon" onClick={copyReferralCode}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="secondary" size="icon" onClick={shareReferralCode}>
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referral List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">My Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No referrals yet. Share your code to earn rewards!</p>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((ref) => (
                      <div key={ref.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{ref.referred_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          {ref.reward_amount && (
                            <p className="font-semibold text-green-600">+₹{ref.reward_amount}</p>
                          )}
                          <Badge variant={ref.status === 'credited' ? 'default' : 'outline'}>
                            {ref.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Redeem Tab */}
          <TabsContent value="redeem" className="mt-4">
            <WalletRedemptionSection 
              userType="patient" 
              token={localStorage.getItem('patient_token')} 
              walletBalance={wallet?.balance || 0}
              onRedemptionSuccess={() => fetchData()}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
